import React, { useState, useEffect, useMemo, useCallback } from "react";

// Context Hooks
import { useUser } from "../../contexts/UserContext";
import { useFamily } from "../../domain/services/hooks/useFamily";

// UI Components
import PlaidLinkButton from "../../components/common/PlaidLinkButton";
import LoadingState from "../../components/common/LoadingState";
import ErrorState from "../../components/common/ErrorState";
import { useUserBanks } from "../../domain/services/hooks/useUserBanks";
import { useBankAccounts } from "../../domain/services/hooks/useBankAccounts";
import { deleteUserBankAccountAPI } from "../../infrastructure/api/userBankAccounts";
import BankAccountsList from "../../components/BankAccountsList";
import FamilyMemberSection from "../../components/FamilyMemberSection";
import { BankAccount } from "../../domain/types/banks/BankAccount";
import { useBanks } from "../../domain/services/hooks/useBanks";
import { useFamilyMembers } from "../../domain/services/hooks/useFamilyMembers";
import { useFamilyMemberAccounts } from "../../domain/services/hooks/useFamilyMemberAccounts";
import { HiOutlineBanknotes } from "react-icons/hi2";

const ConnectedAccounts: React.FC = () => {
  const { currentUser, appUser } = useUser();
  const {
    userBanks,
    loading: banksLoading,
    error: banksError,
  } = useUserBanks();
  const {
    bankAccounts,
    loading: accountsLoading,
    error: accountsError,
  } = useBankAccounts();
  const [localBankAccounts, setLocalBankAccounts] = useState<BankAccount[]>([]);
  const { family, loading: familyLoading } = useFamily();
  const [deletingAccountIds, setDeletingAccountIds] = useState<Set<string>>(
    new Set(),
  );

  // Sync local state with fetched bank accounts
  useEffect(() => {
    if (!accountsLoading && bankAccounts) {
      setLocalBankAccounts(bankAccounts);
    }
  }, [bankAccounts, accountsLoading]);

  const {
    banks,
    loading: banksDataLoading,
    error: banksDataError,
  } = useBanks(userBanks);
  const {
    familyMembers,
    loading: familyMembersLoading,
    error: familyMembersError,
  } = useFamilyMembers(family);
  const {
    familyMemberAccounts,
    loading: familyAccountsLoading,
    error: familyAccountsError,
  } = useFamilyMemberAccounts(family, familyMembers, appUser?.appUserId);

  const isLoading =
    banksLoading ||
    accountsLoading ||
    banksDataLoading ||
    familyLoading ||
    familyMembersLoading ||
    familyAccountsLoading;
  const error =
    banksError ||
    accountsError ||
    banksDataError ||
    familyMembersError ||
    familyAccountsError;

  const handleDeleteAccount = useCallback(
    async (accountId: string) => {
      try {
        setDeletingAccountIds((prev) => new Set(prev).add(accountId));

        // Optimistic update
        setLocalBankAccounts((prev) =>
          prev.filter((acc) => acc.accountId !== accountId),
        );

        if (currentUser) {
          const accessToken = await currentUser.getIdToken();
          await deleteUserBankAccountAPI(accessToken, accountId);
        }
      } catch (error) {
        console.error("Error deleting bank account:", error);
        // Rollback on error
        setLocalBankAccounts(bankAccounts);
      } finally {
        setDeletingAccountIds((prev) => {
          const updated = new Set(prev);
          updated.delete(accountId);
          return updated;
        });
      }
    },
    [currentUser, bankAccounts],
  );
  const userAccountsByBank = useMemo(() => {
    // Initialize with empty array if localBankAccounts is undefined
    const accounts = localBankAccounts ?? [];

    // Helper function to group accounts by bankId
    const groupedAccounts = accounts.reduce<{
      [bankId: string]: BankAccount[];
    }>((acc, account) => {
      if (!acc[account.bankId]) {
        acc[account.bankId] = [];
      }
      acc[account.bankId].push(account);
      return acc;
    }, {});

    return groupedAccounts;
  }, [localBankAccounts]);

  const allBanks = useMemo(() => ({ ...(banks || {}) }), [banks]);

  const sharedAccounts = useMemo(() => {
    const accountCounts = new Map<string, number>();

    const familyAccounts = familyMemberAccounts
      ? Object.values(familyMemberAccounts).flat()
      : [];
    const userAccounts = localBankAccounts || [];
    const allAccounts = [...familyAccounts, ...userAccounts];

    allAccounts.forEach((account) => {
      if (account && account.bankId && account.mask) {
        const key = `${account.bankId}-${account.mask}`;
        accountCounts.set(key, (accountCounts.get(key) || 0) + 1);
      }
    });

    return new Set(
      Array.from(accountCounts.entries())
        .filter(([_, count]) => count > 1)
        .map(([accountKey]) => accountKey),
    );
  }, [familyMemberAccounts, localBankAccounts]);

  // Loading check comes after all hooks
  if (isLoading) {
    return <LoadingState message="Loading bank accounts..." />;
  }

  if (error) {
    return <ErrorState error={error as Error} />;
  }
  // Helper function to group accounts by bankId
  const groupAccountsByBank = (accounts: BankAccount[]) => {
    return accounts.reduce<{ [bankId: string]: BankAccount[] }>(
      (acc, account) => {
        if (!acc[account.bankId]) {
          acc[account.bankId] = [];
        }
        acc[account.bankId].push(account);
        return acc;
      },
      {},
    );
  };

  // Render family member accounts
  const renderFamilyMemberAccounts = () => {
    return Object.entries(familyMemberAccounts).map(([memberId, accounts]) => {
      if (accounts.length === 0) return null;
      const member = familyMembers[memberId];

      if (!member || memberId === appUser?.appUserId) return null;

      const accountsByBank = groupAccountsByBank(accounts);

      return (
        <FamilyMemberSection
          key={memberId}
          member={member}
          accountsByBank={accountsByBank}
          banks={allBanks}
          sharedAccounts={sharedAccounts}
        />
      );
    });
  };

  return (
    <>
      <section className="relative pb-10">
        <div className="container relative mx-auto px-4 mt-8">
          <div className="rounded-xl shadow-lg dark:shadow-gray-800 bg-white dark:bg-slate-900">
            <div className="flex flex-col md:flex-row md:justify-between items-center p-6 border-b border-gray-100 dark:border-gray-800">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                  <HiOutlineBanknotes className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div>
                  <h5 className="text-xl font-semibold">
                    Connected Bank Accounts
                  </h5>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Manage your connected financial accounts
                  </p>
                </div>
              </div>

              <PlaidLinkButton>
                <span>Connect Bank Account</span>
              </PlaidLinkButton>
            </div>

            {bankAccounts.length > 0 && (
              <div className="p-6">
                <BankAccountsList
                  banks={allBanks}
                  accountsByBank={userAccountsByBank}
                  sharedAccounts={sharedAccounts}
                  deletingAccountIds={deletingAccountIds}
                  onDeleteAccount={handleDeleteAccount}
                  isOwner={true}
                />
              </div>
            )}
          </div>
        </div>
      </section>
      {renderFamilyMemberAccounts()}
    </>
  );
};

export default ConnectedAccounts;
